/**
 * Smart Hub App Logger — standard logging for all frontend tools.
 *
 * STANDARD FORMAT (every log line):
 *   [ToolName] LEVEL [CODE] message
 *
 * RULES:
 * - Use createLogger('ToolName') in each component/tool; call .debug(), .info(), .warn(), .error().
 * - In production (NODE_ENV=production): only .error() and .warn() are emitted; .debug() and .info() are no-ops.
 * - Never log PII (user id, org id, email, tokens) in production. Pass context only in development.
 * - For errors, always pass an error code (e.g. E_IDC_001) so support and pen-test can trace issues.
 * - For SOC 2 audit trail: use log.securityAudit(category, code, message) for access denied, hard cap,
 *   invalid input, etc. S_* codes are always emitted in production (no PII in message).
 *
 * USAGE:
 *   import { createLogger } from '../utils/appLogger';
 *   const log = createLogger('IdChecker');
 *   log.info('Tab', 'Usage tab loaded');
 *   log.error('API', 'E_IDC_002', 'Failed to fetch workspaces', err);
 */

const isProduction = typeof process !== 'undefined' && process.env.NODE_ENV === 'production';
const isDev = !isProduction;

/** In production, debug/info only if user enabled via console: window.enableSmartHubDebug() */
function isDebugEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('smartHub_debug') === 'true';
  } catch {
    return false;
  }
}

/** Strip known PII/sensitive keys from an object (for any context passed to logs). Enterprise/OWASP: no PII in browser logs. */
function sanitize(context) {
  if (context == null || typeof context !== 'object') return context;
  const safe = { ...context };
  const piiKeys = [
    'user_id', 'userId', 'organization_id', 'orgId', 'email', 'token', 'access_token', 'refresh_token', 'password',
    'profile', 'activePlan', 'linked_email', 'buyer_email', 'smarthubEmail', 'user_email', 'first_name', 'last_name',
    'reseller_subscription_id', 'resellerSubscriptionId', 'buyerEmail', 'emailsToAdd', 'emailsToRemove', 'message', 'messageSnippet', 'errorSnippet',
    'client_secret', 'stripe_customer_id', 'billing_contact_email', 'billing_contact_phone',
    'billing_address_line1', 'billing_address_line2', 'billing_city', 'billing_state_province',
    'billing_postal_code', 'vat_number', 'owner_email', 'owner_first_name', 'owner_last_name',
    'phone', 'address_street', 'address_suite', 'name', 'slug',
    'config_id', 'configId', 'link_id', 'linkId', 'workspace_id', 'worksheet_id',
  ];
  piiKeys.forEach(k => {
    if (k in safe) safe[k] = '[REDACTED]';
  });
  return safe;
}

/**
 * Safe subset of a `products_with_creators` (or similar catalog) row for debug logs.
 * Omits URLs, descriptions, creator display strings, and person-linked UUIDs — suitable for prod pen-test posture.
 */
export function summarizeCatalogProductRow(row) {
  if (row == null || typeof row !== 'object') return {};
  return {
    product_slug: row.product_slug,
    product_id: row.product_id,
    version: row.version,
    subversion: row.subversion,
    coming_soon: row.coming_soon,
    admin_dev_only: row.admin_dev_only,
    display_new: row.display_new,
    creator_status: row.creator_status,
    product_industries_count: Array.isArray(row.product_industries) ? row.product_industries.length : undefined,
  };
}

/**
 * Safe subset of an `imagetocell_config` row for debug logs (create/save flows).
 * Omits UUIDs, workspace/worksheet ids, names, and user/org linkage.
 */
export function summarizeI2CConfigRowForLog(row) {
  if (row == null || typeof row !== 'object') return {};
  return {
    processing_mode: row.processing_mode,
    config_type: row.config_type,
    paused: row.paused,
    shared_with_org: row.shared_with_org,
    images_to_process: row.images_to_process,
    namemap_engage_lock: row.namemap_engage_lock,
  };
}

/**
 * Create a logger bound to a tool/module name.
 * @param {string} toolName - e.g. 'IdChecker', 'BulkAttach', 'CellHistory', 'Billing'
 * @returns {{ debug, info, warn, error }}
 */
export function createLogger(toolName) {
  const prefix = () => `[${toolName}]`;

  const formatLine = (level, code, message) => {
    const codePart = code ? ` [${code}]` : '';
    return `${prefix()} ${level}${codePart} ${message}`;
  };

  return {
    /** Only emitted in development or when debug flag is set. Do not log PII. */
    debug(category, message, context = null) {
      if (!isDev && !isDebugEnabled()) return;
      const safe = context != null ? sanitize(context) : null;
      const line = formatLine('DEBUG', null, `[${category}] ${message}`);
      if (safe != null) console.debug(line, safe);
      else console.debug(line);
    },

    /** Only emitted in development or when debug flag is set. Use for flow/trace. Do not log PII. */
    info(category, message, context = null) {
      if (!isDev && !isDebugEnabled()) return;
      const safe = context != null ? sanitize(context) : null;
      const line = formatLine('INFO', null, `[${category}] ${message}`);
      if (safe != null) console.log(line, safe);
      else console.log(line);
    },

    /** Emitted in all environments. Use for recoverable issues. Do not log PII. */
    warn(category, message, context = null) {
      const safe = context != null ? sanitize(context) : null;
      const line = formatLine('WARN', null, `[${category}] ${message}`);
      if (safe != null) console.warn(line, safe);
      else console.warn(line);
    },

    /**
     * Emitted in all environments. Use for failures that need support/pen-test trace.
     * @param {string} category - e.g. 'API', 'Auth', 'Billing'
     * @param {string} code - Error code (e.g. 'E_IDC_001'). See errorCodes.js.
     * @param {string} message - Short human-readable message (no PII).
     * @param {Error|null} err - Optional Error object; message will be logged, stack only in dev.
     */
    error(category, code, message, err = null) {
      const line = formatLine('ERROR', code, `[${category}] ${message}`);
      console.error(line);
      if (err && err instanceof Error) {
        console.error(`  message: ${err.message}`);
        if (isDev && err.stack) console.error(err.stack);
      }
    },

    /**
     * Security-audit event (SOC 2 CC7.1). Always emitted in all environments.
     * Use for: access denied, hard cap reached, invalid input rejected. No PII in message.
     * @param {string} category - e.g. 'Access', 'Subscription'
     * @param {string} code - Security code (e.g. S_IDC_001). See errorCodes.js.
     * @param {string} message - Short, non-PII description.
     */
    securityAudit(category, code, message) {
      const line = formatLine('SECURITY', code, `[${category}] ${message}`);
      console.warn(line);
    },
  };
}

/**
 * Default logger for app-level messages (no tool name).
 * Use when the code is not inside a specific tool (e.g. App.js, UserContext).
 */
export const appLogger = createLogger('App');

if (!isProduction && typeof window !== 'undefined') {
  window.enableSmartHubDebug = () => {
    try {
      window.localStorage.setItem('smartHub_debug', 'true');
      console.warn('[App] WARN [DEBUG] Debug logging enabled. All tools will emit info/debug logs until disabled.');
    } catch (e) {
      console.warn('[App] WARN Could not enable debug:', e.message);
    }
  };
  window.disableSmartHubDebug = () => {
    try {
      window.localStorage.removeItem('smartHub_debug');
      console.warn('[App] WARN [DEBUG] Debug logging disabled.');
    } catch (e) {
      console.warn('[App] WARN Could not disable debug:', e.message);
    }
  };
}

export default createLogger;
